import React, { useState } from 'react';
import EmptyState from '../common/EmptyState';

// What the student-facing labels call each piece a comment can be on. A kind
// missing here gets no label rather than its raw enum on the page.
const SUBJECT_LABELS = {
  tara_essay: 'TARA essay',
  lnat_essay: 'LNAT essay',
  case_study: 'Case study',
};

/**
 * "On: TARA essay" for a comment on one piece: a button that opens that
 * piece in the Activity tab through `onOpenPiece({ kind, id })` (the
 * profile switches to Activity, where an essay's Writing row or a case
 * answer's view opens). Never a bare "#…" link, which the page's <base>
 * tag would send to the landing page. Without `onOpenPiece` the subject is
 * plain text. Labelled by kind only: the tracker payload carries no date
 * for the piece, and the comment's own date is already shown.
 */
const CommentSubject = ({ kind, id, onOpenPiece }) => {
  const label = SUBJECT_LABELS[kind];
  if (!label || !id) return null;
  const text = `On: ${label}`;
  return (
    <p className="td-comment__subject">
      {onOpenPiece
        ? <button type="button" onClick={() => onOpenPiece({ kind, id })}>{text}</button>
        : text}
    </p>
  );
};

/**
 * Comment timeline plus a composer.
 *
 * The read path returns only teacher_user_id -- no author name -- so we can
 * label the current user's own comments "You" and must leave the rest generic.
 * The POST response returns { id, created_at, subject_kind, subject_id } but
 * not the text, so the optimistic row is built from what we sent.
 */
const StudentFeedbackPanel = ({
  comments, loading, forbidden, error, currentUserId, onPostComment, posting, onOpenPiece,
}) => {
  const [text, setText] = useState('');

  if (loading) return <p className="td-loading">Loading feedback…</p>;
  if (forbidden) {
    return (
      <EmptyState
        tone="warning"
        title="You don't have access to this student's feedback"
        body="Feedback is visible only to teachers who share a group with the student."
      />
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    const ok = await onPostComment(value);
    if (ok) setText('');
  };

  const rows = Array.isArray(comments) ? comments : [];

  return (
    <div className="td-feedback">
      <form className="td-feedback__composer" onSubmit={submit}>
        <label className="td-field">
          <span>Add feedback</span>
          <textarea
            value={text}
            rows={3}
            onChange={(e) => setText(e.target.value)}
            placeholder="Visible to the student and to other teachers in this group."
          />
        </label>
        <button type="submit" className="td-btn td-btn--primary" disabled={posting || !text.trim()}>
          {posting ? 'Posting…' : 'Post feedback'}
        </button>
      </form>

      {error ? <div className="td-error">{error}</div> : null}

      {rows.length === 0 ? (
        <EmptyState title="No feedback yet" body="Comments you post appear here, newest first." />
      ) : (
        <ul className="td-commentlist">
          {rows.map((c) => {
            const mine = currentUserId && String(c.teacher_user_id) === String(currentUserId);
            return (
              <li key={c.id} className="td-comment">
                <div className="td-comment__head">
                  <span className="td-comment__author">{mine ? 'You' : 'Teacher'}</span>
                  <span className="td-comment__date">
                    {c.created_at ? String(c.created_at).slice(0, 10) : ''}
                  </span>
                </div>
                <CommentSubject kind={c.subject_kind} id={c.subject_id} onOpenPiece={onOpenPiece} />
                <p className="td-comment__body">{c.comment_text}</p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default StudentFeedbackPanel;
